import React, { useCallback, useEffect, useMemo, useRef } from 'react';

const SQL_KEYWORDS = new Set([
  'SELECT','FROM','WHERE','AND','OR','NOT','IN','IS','NULL','TRUE','FALSE',
  'GROUP','BY','ORDER','HAVING','LIMIT','OFFSET','AS','ON','JOIN','LEFT','RIGHT',
  'INNER','OUTER','FULL','CROSS','UNION','ALL','DISTINCT','CASE','WHEN','THEN',
  'ELSE','END','BETWEEN','LIKE','ILIKE','EXISTS','WITH','VALUES','CAST','AT',
  'INTERVAL','ASC','DESC','OVER','PARTITION','RANGE','ROWS','UNBOUNDED','PRECEDING',
  'FOLLOWING','CURRENT','ROW','LATERAL','USING','QUALIFY','EXCLUDE','TOP','SAMPLE',
]);
const SQL_FUNCTIONS = new Set([
  'COUNT','SUM','AVG','MIN','MAX','MEDIAN','STDDEV','VARIANCE','ANY_VALUE',
  'COALESCE','NULLIF','GREATEST','LEAST','ROUND','FLOOR','CEIL','CEILING','ABS',
  'CONCAT','LOWER','UPPER','LENGTH','SUBSTR','SUBSTRING','TRIM','REPLACE','SPLIT',
  'TO_CHAR','TO_DATE','TO_TIMESTAMP','DATE_TRUNC','DATEADD','DATEDIFF',
  'CURRENT_DATE','CURRENT_TIMESTAMP','NOW','EXTRACT','YEAR','MONTH','DAY','HOUR',
  'MINUTE','QUARTER','WEEK','RANK','DENSE_RANK','ROW_NUMBER','LAG','LEAD',
  'FIRST_VALUE','LAST_VALUE','NTH_VALUE','LISTAGG','ARRAY_AGG','PERCENTILE_CONT',
]);

function escapeHtml(s) {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
}

function highlight(src) {
  if (!src) return '';
  // Single-pass tokenizer. Order matters: comments → strings → numbers → identifiers.
  const tokens = [];
  let i = 0;
  const len = src.length;
  while (i < len) {
    const c = src[i];
    // line comment
    if (c === '-' && src[i + 1] === '-') {
      let j = i; while (j < len && src[j] !== '\n') j++;
      tokens.push({ t: 'com', v: src.slice(i, j) }); i = j; continue;
    }
    // block comment
    if (c === '/' && src[i + 1] === '*') {
      let j = i + 2; while (j < len - 1 && !(src[j] === '*' && src[j + 1] === '/')) j++;
      j = Math.min(len, j + 2);
      tokens.push({ t: 'com', v: src.slice(i, j) }); i = j; continue;
    }
    // string (single quote, supports doubled escape)
    if (c === "'") {
      let j = i + 1;
      while (j < len) {
        if (src[j] === "'") { if (src[j + 1] === "'") { j += 2; continue; } j++; break; }
        j++;
      }
      tokens.push({ t: 'str', v: src.slice(i, j) }); i = j; continue;
    }
    // double-quoted identifier (Snowflake)
    if (c === '"') {
      let j = i + 1;
      while (j < len && src[j] !== '"') j++;
      j = Math.min(len, j + 1);
      tokens.push({ t: 'str', v: src.slice(i, j) }); i = j; continue;
    }
    // number
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src[i + 1] || ''))) {
      let j = i; while (j < len && /[0-9.eE+\-_]/.test(src[j])) j++;
      tokens.push({ t: 'num', v: src.slice(i, j) }); i = j; continue;
    }
    // identifier / keyword
    if (/[A-Za-z_]/.test(c)) {
      let j = i; while (j < len && /[A-Za-z0-9_]/.test(src[j])) j++;
      const word = src.slice(i, j);
      const upper = word.toUpperCase();
      let cls = 'id';
      if (SQL_KEYWORDS.has(upper)) cls = 'kw';
      else if (SQL_FUNCTIONS.has(upper) && src[j] === '(') cls = 'fn';
      tokens.push({ t: cls, v: word }); i = j; continue;
    }
    // anything else (whitespace, punctuation)
    let j = i; while (j < len && !/[A-Za-z_0-9'"\-/]/.test(src[j])) j++;
    if (j === i) j = i + 1;
    tokens.push({ t: 'pun', v: src.slice(i, j) });
    i = j;
  }
  return tokens.map(({ t, v }) => {
    const escaped = escapeHtml(v);
    if (t === 'pun') return escaped;
    return `<span class="dx-sql-tok-${t}">${escaped}</span>`;
  }).join('');
}

export default function SqlEditor({
  value,
  onChange,
  onRun,
  onSave,
  toolbar = null,
  variant = 'dark',     // 'dark' | 'light'
  placeholder,
  rowsHint,             // e.g. "12 rows × 2 cols · 380 ms"
  ariaLabel = 'SQL editor',
}) {
  const taRef = useRef(null);
  const preRef = useRef(null);
  const gutterRef = useRef(null);

  const html = useMemo(() => highlight(value || '') + '\n', [value]);
  const lineCount = useMemo(() => Math.max(1, (value || '').split('\n').length), [value]);

  // Sync scroll position from the textarea to the highlight layer + gutter.
  const onScroll = useCallback(() => {
    const ta = taRef.current; const pre = preRef.current; const g = gutterRef.current;
    if (!ta) return;
    if (pre) {
      pre.scrollTop = ta.scrollTop;
      pre.scrollLeft = ta.scrollLeft;
    }
    if (g) g.scrollTop = ta.scrollTop;
  }, []);

  // Tab key inserts two spaces instead of moving focus.
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.currentTarget;
      const start = ta.selectionStart, end = ta.selectionEnd;
      const next = (value || '').slice(0, start) + '  ' + (value || '').slice(end);
      onChange?.(next);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = start + 2; });
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onRun?.();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      onSave?.();
    }
  }, [value, onChange, onRun, onSave]);

  // Re-sync scroll if the value changes externally (e.g. AI assist applies SQL).
  useEffect(() => { onScroll(); }, [value, onScroll]);

  return (
    <div className={`dx-sql-editor ${variant === 'light' ? 'light' : ''}`}>
      <div className="dx-sql-toolbar">
        <span style={{ fontWeight: 600 }}>SQL</span>
        {rowsHint && <><span style={{ opacity: 0.5 }}>·</span><span style={{ opacity: 0.85 }}>{rowsHint}</span></>}
        <span style={{ flex: 1 }} />
        {toolbar}
      </div>
      <div className="dx-sql-body">
        <div ref={gutterRef} className="dx-sql-gutter" aria-hidden="true">
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        <div className="dx-sql-stack">
          <pre ref={preRef} className="dx-sql-pre" aria-hidden="true"
               dangerouslySetInnerHTML={{ __html: html }} />
          <textarea
            ref={taRef}
            className="dx-sql-input"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            onScroll={onScroll}
            onKeyDown={onKeyDown}
            spellCheck={false}
            placeholder={placeholder}
            aria-label={ariaLabel}
            wrap="off"
          />
        </div>
      </div>
    </div>
  );
}
